"use strict";
import { InterfaceEventName, InterfaceSectionName } from "@uniswap/analytics-events";
import { useModalState } from "hooks/useModalState";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Text, TouchableArea, useMedia, useScrollbarStyles, useSporeColors } from "ui/src";
import { Modal } from "components/modals/Modal";
import { useUpdateScrollLock } from "components/modals/ScrollLock";
import { FeatureFlags } from "features/gating/flags";
import { useFeatureFlag } from "features/gating/hooks";
import { SearchModalNoQueryList } from "features/search/SearchModal/SearchModalNoQueryList";
import { SearchModalResultsList } from "features/search/SearchModal/SearchModalResultsList";
import { useFilterCallbacks } from "features/search/SearchModal/hooks/useFilterCallbacks";
import { SearchTab, WEB_SEARCH_TABS } from "features/search/SearchModal/types";
import { SearchTextInput } from "features/search/SearchTextInput";
import { ModalName } from "features/telemetry/constants";
import { sendAnalyticsEvent } from "features/telemetry/send";
import { useTrace } from "utilities/src/telemetry/trace/TraceContext";
import { useDebounce } from "utilities/src/time/timing";
export const SearchModal = memo(function _SearchModal() {
  const poolSearchEnabled = useFeatureFlag(FeatureFlags.PoolSearch);
  const colors = useSporeColors();
  const { t } = useTranslation();
  const media = useMedia();
  const scrollbarStyles = useScrollbarStyles();
  const { isOpen: isModalOpen, toggleModal: toggleSearchModal } = useModalState(ModalName.Search);
  const [activeTab, setActiveTab] = useState(poolSearchEnabled ? SearchTab.All : SearchTab.Tokens);
  const { onChangeText, searchFilter, chainFilter, parsedChainFilter, parsedSearchFilter } = useFilterCallbacks(null, ModalName.Search);
  const debouncedSearchFilter = useDebounce(searchFilter);
  const debouncedParsedSearchFilter = useDebounce(parsedSearchFilter);
  const trace = useTrace({ section: InterfaceSectionName.NAVBAR_SEARCH });
  const onClose = useCallback(() => {
    toggleSearchModal();
    sendAnalyticsEvent(InterfaceEventName.NAVBAR_SEARCH_EXITED, {
      navbar_search_input_text: debouncedSearchFilter ?? "",
      hasInput: Boolean(debouncedSearchFilter),
      ...trace
    });
  }, [toggleSearchModal, debouncedSearchFilter, trace]);
  useUpdateScrollLock({ isModalOpen });
  return <Modal
    extendOnKeyboardVisible
    fullScreen
    hideKeyboardOnDismiss
    hideKeyboardOnSwipeDown
    renderBehindBottomInset
    backgroundColor={colors.surface1.val}
    isModalOpen={isModalOpen}
    maxWidth={680}
    maxHeight={520}
    name={ModalName.Search}
    padding="$none"
    height={"100%"}
    onClose={onClose}
  >
      <Flex grow style={scrollbarStyles}>
        <Flex
    $sm={{ px: "$spacing16", py: "$spacing4", borderColor: void 0, borderBottomWidth: 0 }}
    px="$spacing4"
    py="$spacing20"
    borderBottomColor="$surface3"
    borderBottomWidth={1}
  >
          <SearchTextInput
    autoFocus
    minHeight={media.sm ? void 0 : 24}
    backgroundColor={media.sm ? "$surface2" : "$none"}
    borderColor={!media.sm ? "$none" : void 0}
    py="$none"
    placeholder={poolSearchEnabled ? t("search.input.placeholder") : t("tokens.selector.search.placeholder")}
    px="$spacing16"
    value={searchFilter ?? ""}
    onChangeText={onChangeText}
    onKeyPress={(e) => {
      if (["Enter", "ArrowUp", "ArrowDown"].includes(e.nativeEvent.key)) {
        e.preventDefault();
      }
    }}
  />
        </Flex>
        {poolSearchEnabled && <Flex row px="$spacing20" pt="$spacing16" pb="$spacing8" gap="$spacing16">
            {WEB_SEARCH_TABS.map((tab) => <TouchableArea key={tab} onPress={() => setActiveTab(tab)}>
                <Text color={activeTab === tab ? "$neutral1" : "$neutral2"} variant="buttonLabel2">
                  {tab}
                </Text>
              </TouchableArea>)}
          </Flex>}
        <Flex grow height={"60vh"}>
          {searchFilter && searchFilter.length > 0 ? <SearchModalResultsList
    chainFilter={chainFilter ?? parsedChainFilter}
    debouncedParsedSearchFilter={debouncedParsedSearchFilter}
    debouncedSearchFilter={debouncedSearchFilter}
    searchFilter={searchFilter}
    activeTab={activeTab}
    onSelect={onClose}
  /> : <SearchModalNoQueryList chainFilter={chainFilter} activeTab={activeTab} onSelect={onClose} />}
          
        </Flex>
      </Flex>
    </Modal>;
});
